import React, { useMemo, useState } from 'react';
import { Badge, CopyButton, EmptyState, Pagination } from '../ui';
import { ago } from '../../observability/contract';
import { clock, duration } from './units';

const shortDigest = (d) => (d ? String(d).replace(/^sha256:/, '').slice(0, 12) : '—');

const DEPLOY_STATUS = {
  successful: { tone: 'ok', label: 'Successful' },
  in_progress: { tone: 'info', label: 'In progress' },
  stopping: { tone: 'neutral', label: 'Stopping' },
  stopped: { tone: 'neutral', label: 'Stopped' },
  rolling_back: { tone: 'warn', label: 'Rolling back' },
  rolled_back: { tone: 'warn', label: 'Rolled back' },
  failed: { tone: 'danger', label: 'Failed' },
};
const HISTORY_PAGE = 20;

const IMAGE_STATUS = {
  stale: <Badge tone="warn" icon="alert">Behind :latest</Badge>,
  // No task is running, so there is no digest to compare: the service is scaled to zero.
  idle: <Badge tone="neutral">No task running</Badge>,
  current: <Badge tone="ok" dot>Current</Badge>,
};

function Images({ images }) {
  const stale = images.filter(i => i.status === 'stale').length;
  return (
    <>
      <div className={`notice ${stale ? 'notice-warn' : 'notice-ok'} mb-3`} role="status">
        <div className="notice-body">
          {stale
            ? <><strong>{stale} {stale === 1 ? 'service is' : 'services are'} running an older image than :latest.</strong> A
                green deploy does not prove a service rolled: only the digest of the running task does. Roll it
                with a force-new-deployment on that service.</>
            : 'Every running service is on the image :latest points at.'}
        </div>
      </div>
      <div className="card card-flush mb-4">
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th scope="col">Service</th>
                <th scope="col">Repository</th>
                <th scope="col">Running</th>
                <th scope="col">Digest running</th>
                <th scope="col">Digest of :latest</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {images.map(i => (
                <tr key={`${i.service}-${i.container || ''}-${i.repository}`}>
                  <td data-label="Service" className="fw-600">
                    {i.service}
                    {i.container && i.container !== i.service && <span className="cell-sub">{i.container}</span>}
                  </td>
                  <td data-label="Repository"><span className="mono text-sm">{i.repository}</span></td>
                  <td data-label="Running">
                    <span className="mono">{i.running_tag || '—'}</span>
                    {i.pushed_at && (
                      <span className="cell-sub">{i.status === 'idle' ? ':latest pushed' : 'pushed'} {ago(i.pushed_at)}</span>
                    )}
                  </td>
                  <td data-label="Digest running">
                    <span className="row gap-1 nowrap">
                      <span className={`mono text-xs${i.status === 'stale' ? ' text-warn fw-600' : ''}`}>{shortDigest(i.running_digest)}</span>
                      {i.running_digest && <CopyButton value={i.running_digest} label={`Copy running digest of ${i.service}`} />}
                    </span>
                  </td>
                  <td data-label="Digest of :latest"><span className="mono text-xs">{shortDigest(i.latest_digest)}</span></td>
                  <td data-label="Status">{IMAGE_STATUS[i.status] || <Badge tone="neutral">{i.status}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// One row per ECS service deployment: when a service was rolled, to which image, and
// how the rollout ended. ECS keeps this history itself (90 days), in every account.
function DeployHistory({ history }) {
  const [service, setService] = useState('');
  const [page, setPage] = useState(1);
  const services = useMemo(() => [...new Set(history.map(h => h.service))].sort(), [history]);
  const rows = service ? history.filter(h => h.service === service) : history;
  const pages = Math.max(1, Math.ceil(rows.length / HISTORY_PAGE));
  const current = Math.min(page, pages);
  const visible = rows.slice((current - 1) * HISTORY_PAGE, current * HISTORY_PAGE);

  if (!history.length) {
    return (
      <div className="card mb-4">
        <EmptyState icon="cloudUpload" title="No deployments recorded">
          ECS keeps each service's deployments for 90 days; none were found.
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="card card-flush mb-4">
      <div className="toolbar">
        <label className="row gap-2 text-sm" htmlFor="deploy-service">
          <span className="text-muted">Service</span>
          <select id="deploy-service" className="form-input" style={{ width: 'auto' }} value={service}
                  onChange={(e) => { setService(e.target.value); setPage(1); }}>
            <option value="">All services</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="toolbar-spacer" />
        <span className="text-sm text-muted num">{rows.length} {rows.length === 1 ? 'deployment' : 'deployments'}</span>
      </div>
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">Service</th>
              <th scope="col">Image</th>
              <th scope="col">Result</th>
              <th scope="col">Took</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(d => {
              const st = DEPLOY_STATUS[d.status] || { tone: 'neutral', label: d.status };
              const took = d.started_at && d.finished_at ? (d.finished_at - d.started_at) / 1000 : null;
              return (
                <tr key={d.id}>
                  <td data-label="Started" className="nowrap">
                    <span title={clock(d.started_at || d.created_at)}>{ago(d.started_at || d.created_at)}</span>
                    <span className="cell-sub">{clock(d.started_at || d.created_at)}</span>
                  </td>
                  <td data-label="Service" className="fw-600">
                    {d.service}
                    <span className="cell-sub mono">{d.task_definition}</span>
                  </td>
                  <td data-label="Image">
                    {(d.images || []).map(img => (
                      <div key={`${d.id}-${img.container}`} className="row row-wrap gap-1">
                        <span className="mono">{img.tag || (img.expired ? 'expired from ECR' : 'untagged')}</span>
                        <span className="mono text-xs text-muted">{shortDigest(img.digest)}</span>
                        {d.images.length > 1 && <span className="text-xs text-faint">{img.container}</span>}
                      </div>
                    ))}
                  </td>
                  <td data-label="Result">
                    <Badge tone={st.tone} dot>{st.label}</Badge>
                    {d.reason && d.status !== 'successful' && <span className="cell-sub">{d.reason}</span>}
                  </td>
                  <td data-label="Took" className="nowrap num">
                    {took == null ? '—' : took < 60 ? `${Math.round(took)} s` : duration(took)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={current} pageSize={HISTORY_PAGE} total={rows.length} onPage={setPage} noun="deployments" />
    </div>
  );
}

export default function DeploymentsPanel({ data }) {
  if (!data) return null;
  return (
    <>
      <div className="section-head">
        <h2>Images</h2>
        <p>what each service runs, against what the registry says is latest</p>
      </div>
      <Images images={data.images || []} />

      <div className="section-head">
        <h2>Deploy history</h2>
        <p>when each service was rolled, and to which image</p>
      </div>
      <DeployHistory history={data.history || []} />

      <div className="section-head">
        <h2>Service events</h2>
        <p>recent ECS deployment events, newest first</p>
      </div>
      <div className="card mb-4">
        {(data.events || []).length === 0 ? (
          <EmptyState icon="clock" title="No recent events">Nothing was deployed or scaled in this window.</EmptyState>
        ) : (
          <ol className="event-feed">
            {data.events.map((ev, i) => (
              <li key={`${ev.at}-${ev.service}-${i}`}>
                <span className="event-feed-time num" title={clock(ev.at)}>{ago(ev.at)}</span>
                <span><span className="fw-600">{ev.service}</span> {ev.message}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
}
